import PropTypes from 'prop-types';
import React from 'react';
import EditorialCard from '../../../components/Community/EditorialCard/EditorialCard';
import SingleReviewComponent, {
    MULTIPLE_REVIEW,
} from '../../../components/Community/SingleReviewComponent/SingleReviewComponent';
import CompanyPageLayout from '../../../components/CompanyPages/CompanyPageLayout/CompanyPageLayout';
import EmployerPageJobCallToAction from '../../../components/CompanyPages/Sections/EmployerPageJobCallToAction/EmployerPageJobCallToAction';
import FGBLabel from '../../../components/Elements/FGBLabel/FGBLabel';
import {
    COMPANY_PROPTYPES,
    DEFAULT_COMPANY_PROPS,
} from '../../../components/Includes/CompanyProps';
import { SINGLE_REVIEW_PROPTYPES } from '../../../components/Includes/SingleReviewProps';
import Button from '../../../components/Input/Button/Button';
import { buildImagePath } from '../../../components/JobComponents/Company/Image/logo';
import LeftRailContent from '../../../components/LeftRailLayout/LeftRailContent/LeftRailContent';
import LeftRailLayout from '../../../components/LeftRailLayout/LeftRailLayout';
import { checkPermission } from '../../../utilities/helperFunctions';
import { JobContextProvider } from '../../../contexts/JobContext';
import ArrowPagination from '../../../components/Elements/ArrowPagination/ArrowPagination';
import {
    SnowplowPageViewTracker,
    PAGE_VIEW_SECTIONS,
    PAGE_VIEW_TYPES,
    PAGE_TABS,
} from '../../../components/Analytics/Snowplow/SnowplowPageViewTracker';
import { PageContext } from '../../../contexts/ContextCreator';
import './Review.scss';
import { relatedArticlesProps } from '../../../components/Includes/relatedArticlesProps';
import { PAGE_PROPS } from '../../defaultProps';

export default class Review extends React.PureComponent {
    constructor(props) {
        super();
        this.state = {
            expandAllReviews: props.isBot || false,
        };
    }

    renderReviews = (toggleJobReviewSurvey, wrapAuth, isAuthenticatedUser) => {
        const total = this.props.company.reviewCount;
        const injectedTransforms = {
            small: 'c_scale,f_auto,q_auto,w_93,h_100',
        };
        const firstNumberInSet = 5 * (this.props.page - 1) + 1;
        const lastNumberInSet =
            this.props.page * 5 > total ? total : this.props.page * 5;
        const prevUrl = this.props.pagination.hasPrevPage
            ? this.paginationHandler(this.props.page, 'prev')
            : null;
        const nextUrl = this.props.pagination.hasNextPage
            ? this.paginationHandler(this.props.page, 'next')
            : null;
        const zeroStateImageSrc = buildImagePath(
            '/v1536676783/HomePage/Icons/icon_intel.png',
            'small',
            injectedTransforms
        );

        return (
            <div className="review-box-display">
                {this.props.reviews && this.props.reviews.length ? (
                    this.props.reviews.map((review) => (
                        <SingleReviewComponent
                            {...review}
                            location={MULTIPLE_REVIEW}
                            company={this.props.company}
                            expanded={this.state.expandAllReviews}
                            key={review.title}
                        />
                    ))
                ) : (
                    <div className="zero-state-display d-flex flex-column justify-content-center align-items-center">
                        <img src={zeroStateImageSrc} alt="no review yet" />
                        <FGBLabel>No Reviews Yet</FGBLabel>
                        <div className="text-description">Work(ed) here?</div>
                        <div className="text-description">
                            Be the first to leave an anonymous review
                        </div>
                        <div className="get-started-button">
                            {wrapAuth(
                                'review-button',
                                <Button
                                    type="solid"
                                    value="Get started"
                                    onClick={
                                        isAuthenticatedUser
                                            ? () =>
                                                  toggleJobReviewSurvey(
                                                      true,
                                                      this.props.company
                                                  )
                                            : () => {}
                                    }
                                />,
                                { login_trigger: 'login-to-review' }
                            )}
                        </div>
                    </div>
                )}
                {this.props.pagination.hasPrevPage ||
                this.props.pagination.hasNextPage ? (
                    <ArrowPagination
                        nextPage={nextUrl}
                        prevPage={prevUrl}
                        total={total}
                        numberRange={`${firstNumberInSet} - ${lastNumberInSet}`}
                    />
                ) : null}
            </div>
        );
    };

    paginationHandler = (pageNumber, type) => {
        let url = `/company-reviews/${this.props.company.companyUrl}`;
        switch (type) {
            case 'prev':
                url = `/company-reviews/${this.props.company.companyUrl}?page=${
                    pageNumber - 1
                }`;
                break;
            case 'next':
                url = `/company-reviews/${this.props.company.companyUrl}?page=${
                    pageNumber + 1
                }`;
                break;
            default:
                break;
        }
        return url;
    };

    likeWhatYouSeeSection = () => {
        if (this.props.company.employerInfo.jobCount === 0) {
            return;
        }

        const props = {
            title: `Like what you see? ${this.props.company.companyName} is hiring ...`,
            description: this.props.bannerText,
            jobCount: this.props.company.employerInfo.jobCount,
            button: {
                url: `/company/jobs/${this.props.company.companyUrl}`,
                text: `See all jobs at ${this.props.company.companyName}`,
            },
        };
        return (
            <div className="like-what-you-see-section">
                <EmployerPageJobCallToAction {...props} />
            </div>
        );
    };

    readMoreAboutCompanySection = () => {
        return (
            <div className="read-more-about-company">
                <div className="title">{`Read more about ${this.props.company.companyName}`}</div>
                <div className="content">
                    {this.props.relatedArticles.map((article, idx) => {
                        const align =
                            idx % 2 === 0 ? 'left-side' : 'right-side';
                        return (
                            <div className={`float-column ${align}`}>
                                <EditorialCard {...article} />
                            </div>
                        );
                    })}
                    <div className="clear-float" />
                </div>
            </div>
        );
    };

    expandAllReviews = () => {
        this.setState({
            expandAllReviews: true,
        });
    };

    canViewReviews = () => {
        const session = this.props.PAGE_PROPS.session || {};
        const companyRoles =
            (session.userCompaniesRoles &&
                session.userCompaniesRoles[this.props.company.companyId]) ||
            [];
        const allUserRoles = session.additionalRoles
            ? companyRoles.concat(session.additionalRoles)
            : companyRoles;
        const allowedRoles = [
            'admin',
            'employer',
            'superadmin',
            'fullEditor',
            'customerSuccess',
        ];
        return (
            this.props.isBot ||
            session.leftReview ||
            checkPermission(allUserRoles, allowedRoles)
        );
    };

    render() {
        const {
            pageProps,
            isLoggedIn,
            jobProfileSnippet,
            toggleJobReviewSurvey,
            wrapAuth,
        } = this.context;

        const isAuthenticatedUser = isLoggedIn();
        const showProfileCTA = !(
            isAuthenticatedUser && jobProfileSnippet.isCompleted
        );

        return (
            <div className="page-Employer-Review">
                <SnowplowPageViewTracker
                    pageCompanyId={this.props.company.companyId}
                    pageEntityId={this.props.company.companyId}
                    pageSection={PAGE_VIEW_SECTIONS.COMPANY_PROFILE}
                    pageTab={PAGE_TABS.COMPANY_REVIEWS}
                    pageType={PAGE_VIEW_TYPES.CONTENT}
                />
                <JobContextProvider
                    content={[]}
                    userJobProfile={this.props.userJobProfile}
                >
                    <CompanyPageLayout
                        company={this.props.company}
                        topics={this.props.topics}
                        additionalTopBannerOnlyShownList={['profile-cta']}
                    >
                        <div>
                            <LeftRailLayout>
                                <LeftRailLayout.LeftRail>
                                    <LeftRailContent
                                        {...pageProps.leftRail}
                                        allowProfileCTA={showProfileCTA}
                                    />
                                </LeftRailLayout.LeftRail>
                                <LeftRailLayout.MainContent>
                                    <div className="review-page-header-section">
                                        <div className="title">
                                            Employee Reviews
                                        </div>
                                        {this.canViewReviews() &&
                                        !this.state.expandAllReviews ? (
                                            <div className="expand-all-reviews-button-div">
                                                <Button
                                                    type="secondary-blue-gray"
                                                    value="Show all reviews"
                                                    onClick={
                                                        this.expandAllReviews
                                                    }
                                                />
                                            </div>
                                        ) : null}
                                    </div>
                                    <div className="review-page-description">
                                        (Winged ratings measure job satisfaction
                                        on scale of 1 to 5)
                                    </div>
                                    {this.renderReviews(
                                        toggleJobReviewSurvey,
                                        wrapAuth,
                                        isAuthenticatedUser
                                    )}
                                </LeftRailLayout.MainContent>
                            </LeftRailLayout>
                            <div className="company-controlled-width-container">
                                {this.props.company.isSponsor &&
                                this.props.company.employerInfo
                                    ? this.likeWhatYouSeeSection()
                                    : null}
                                {/* {this.props.company.employerInfo && this.props.relatedArticles ? this.readMoreAboutCompanySection() : null} */}
                            </div>
                        </div>
                    </CompanyPageLayout>
                </JobContextProvider>
            </div>
        );
    }
}
Review.contextType = PageContext;

Review.propTypes = {
    company: PropTypes.shape(COMPANY_PROPTYPES),
    isBot: PropTypes.bool,
    page: PropTypes.number,
    PAGE_PROPS,
    bannerText: PropTypes.string,
    topics: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number,
            title: PropTypes.string,
            url: PropTypes.string,
            layout: PropTypes.string,
            total: PropTypes.number,
            isDefault: PropTypes.number,
            sponsored: PropTypes.bool,
            qaDataAttr: PropTypes.string,
        })
    ),
    userJobProfile: PropTypes.shape({
        hash: PropTypes.string,
        isCompleted: PropTypes.bool,
    }),
    reviews: PropTypes.arrayOf(PropTypes.shape(SINGLE_REVIEW_PROPTYPES)),
    pagination: PropTypes.shape({
        hasPrevPage: PropTypes.bool,
        hasNextPage: PropTypes.bool,
    }),
    relatedArticles: relatedArticlesProps,
};

Review.defaultProps = {
    company: DEFAULT_COMPANY_PROPS,
    isBot: false,
    page: 0,
    userJobProfile: {},
    bannerText: '',
    reviews: [],
    topics: [],
    pagination: {
        hasPrevPage: false,
        hasNextPage: false,
    },
    relatedArticles: [],
    PAGE_PROPS: {
        session: {
            id: null,
            role: null,
        },
    },
};
